/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Dmitriy Kvon
 * @author Andrey Dubov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.13.0
 */

import classNames from 'classnames';
import styled from 'styled-components';

import { BadgeStatus } from '@components/common';
import { LinkTextBlock } from '@components/common/LinkBlock/styled/LinkStyled';
import { colors, fonts } from '@theme/index';

export const SubscriberIdProductPanelStyled = styled.div.attrs({
    className: 'product-panel',
})`
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    column-gap: 16px;
    padding: 24px;

    .product-panel__title {
        grid-column-start: 1;
        grid-column-end: 2;
        margin-bottom: 12px;
    }

    .product-panel__description {
        grid-column: 1;

        margin-bottom: 4px;
    }

    .product-panel__description-link {
        grid-column-start: 2;
        grid-column-end: 3;
    }

    .product-panel__date-connected {
        grid-column-start: 1;
        grid-column-end: 3;

        margin: 12px 0;
    }

    .product-panel__date-plan {
        grid-column-start: 1;
        grid-column-end: 2;
    }

    .product-panel__price {
        grid-column-start: 2;
        grid-column-end: 3;
    }

    .product-panel__btn-connection {
        justify-self: end;
    }
`;

export const ProductCardTabsStyled = styled.div.attrs({
    className: 'product-card__tabs',
})`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 32px;
`;

export const ProductCardTabStyled = styled.div.attrs(({ isActive }: { isActive: boolean }) => ({
    className: classNames('product-card__tab', { active: isActive }),
}))<{ isActive: boolean }>`
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 10px 20px;
    border-radius: 20px;

    &.active {
        background-color: ${colors.textLinkColor};
        color: ${colors.textColorWhite};

        &:hover {
            background-color: ${colors.textLinkColor}90;

            .counter {
                color: ${colors.textLinkColor}90;
            }
        }

        .counter {
            background-color: ${colors.backgroundColorWhite};
            color: ${colors.textLinkColor};
        }
    }

    background-color: ${colors.borderColorDisabled};
    ${fonts.textMainBold};

    &:not(.active):hover {
        color: ${colors.textColorOpacity};

        .counter {
            color: ${colors.textColorOpacity};
        }
    }

    .counter {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${colors.borderColorLight};
        width: 21px;
        height: 21px;
        border-radius: 100%;
        margin-left: 10px;
        ${fonts.textSmallBold};
    }
`;

export const ProductCardCategoryStyled = styled.div.attrs({
    className: 'product-card__category',
})`
    margin-bottom: 32px;

    .product-card__category-title {
        ${fonts.textMainBold};
        color: ${colors.textColorOpacity};
        margin: 16px 0;
    }
`;

export const ProductCardListStyled = styled.div.attrs({
    className: 'product-card__list',
})`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
`;

export const ProductCardStyled = styled.div.attrs({
    className: 'product-card',
})`
    display: flex;
    flex-direction: column;
    padding: 16px;
    box-shadow: 0 0.25px 3px rgba(0, 0, 0, 0.25);
    background-color: ${colors.backgroundColorWhite};
    min-height: 250px;
    width: 271px;

    .product-card__title {
        ${fonts.textMainBold};
        margin-bottom: 8px;
    }

    .product-card__params {
        flex-grow: 1;
        margin-bottom: 24px;
    }

    .product-card__param-link:not(.disabled) {
        ${LinkTextBlock} {
            color: ${colors.textColorDanger};

            &:hover {
                border-bottom-color: ${colors.borderColorError};
            }
        }
    }

    .product-card__description {
        ${fonts.textMain};
        margin-top: 8px;
    }
`;

export const ProductCardParamStyled = styled.div.attrs({
    className: 'product-card__param',
})`
    display: flex;
    flex-direction: column;

    ${fonts.textSmall};
    ${colors.textColor};

    &:not(:last-child) {
        margin-bottom: 4px;
    }

    .product-card__param-label {
        color: ${colors.textColorOpacity};
    }
`;

export const PersonalAccountBadge = styled(BadgeStatus).attrs({
    className: 'product__card__badge',
})`
    ${fonts.textSmall};
    margin-bottom: 16px;
`;
